import { useState } from 'react';

import { Plus, Settings2, Mail, Star, Trash2, Pencil } from 'lucide-react';

import { ReviewConfigModal } from '@/components/review-configs/ReviewConfigModal';
import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useReviewConfigs,
  useCreateReviewConfig,
  useUpdateReviewConfig,
  useDeleteReviewConfig,
} from '@/hooks/useReviewConfigs';
import { extractValidationErrors, extractErrorMessage } from '@/lib/utils';
import { formatDate } from '@/utils/format';

import type { ReviewConfig, CreateReviewConfigDto } from '@repo/shared-types';

export const ReviewConfigsPage = () => {
  usePageTitle('Review Configs');
  const { data: configs = [], isLoading, error } = useReviewConfigs();
  const createMutation = useCreateReviewConfig();
  const updateMutation = useUpdateReviewConfig();
  const deleteMutation = useDeleteReviewConfig();

  const [editingConfig, setEditingConfig] = useState<ReviewConfig | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ReviewConfig | null>(null);
  const [modalErrors, setModalErrors] = useState<Array<{ field: string; message: string }>>([]);
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleNewConfig = () => {
    setEditingConfig(null);
    setModalErrors([]);
    setIsModalOpen(true);
  };

  const handleEditConfig = (config: ReviewConfig) => {
    setEditingConfig(config);
    setModalErrors([]);
    setIsModalOpen(true);
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setModalErrors([]);
      setEditingConfig(null);
    }
  };

  const handleSubmitConfig = async (values: CreateReviewConfigDto) => {
    try {
      setModalErrors([]);
      setIsSaving(true);

      if (editingConfig) {
        // Update existing
        await updateMutation.mutateAsync({
          id: editingConfig.id,
          data: values,
        });
        toast({
          variant: 'success',
          title: 'Review config updated',
          description: `"${values.name}" has been updated successfully.`,
        });
      } else {
        // Create new
        await createMutation.mutateAsync(values);
        toast({
          variant: 'success',
          title: 'Review config created',
          description: `"${values.name}" has been created successfully.`,
        });
      }

      setEditingConfig(null);
      setIsModalOpen(false);
      setModalErrors([]);
    } catch (error) {
      const validationErrors = extractValidationErrors(error);
      if (validationErrors.length > 0) {
        setModalErrors(validationErrors);
      } else {
        toast({
          variant: 'destructive',
          title: editingConfig
            ? 'Failed to update review config'
            : 'Failed to create review config',
          description: extractErrorMessage(error),
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfig = (config: ReviewConfig) => {
    setDeleteTarget(config);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      toast({
        variant: 'success',
        title: 'Review config deleted',
        description: `"${deleteTarget.name}" has been deleted successfully.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete review config',
        description: extractErrorMessage(error),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <Settings2 className="text-destructive mx-auto h-12 w-12 opacity-50" />
          <p className="text-destructive mt-4 font-medium">Failed to load review configs</p>
          <p className="text-muted-foreground mt-2 text-sm">{extractErrorMessage(error)}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading review configs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Reviews</p>
          <h1 className="text-3xl font-bold tracking-tight">Review Configurations</h1>
          <p className="text-muted-foreground max-w-3xl">
            Manage review agent configurations with customizable prompts, retry policies, and
            notification settings.
          </p>
        </div>
        <Button onClick={handleNewConfig}>
          <Plus className="h-4 w-4" />
          New Config
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Review Configurations</CardTitle>
          <CardDescription>
            Configure review agent behavior, prompts, and notification preferences.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {configs.length === 0 ? (
            <div className="text-muted-foreground flex min-h-[200px] flex-col items-center justify-center space-y-4 py-8 text-center">
              <Settings2 className="h-12 w-12 opacity-50" />
              <div>
                <p className="font-medium">No review configurations yet</p>
                <p className="text-sm">Create your first review config to get started.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {configs.map((config) => (
                <div key={config.id} className="rounded-lg border p-4">
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold">{config.name}</p>
                        {config.is_default && (
                          <Badge variant="default" className="gap-1">
                            <Star className="h-3 w-3" />
                            Default
                          </Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm">
                        Max Retries: {config.max_retries} · Created {formatDate(config.created_at)}
                      </p>
                      {config.notification_emails && config.notification_emails.length > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <Mail className="text-muted-foreground h-4 w-4" />
                          <div className="flex flex-wrap gap-1">
                            {config.notification_emails.map((email, idx) => (
                              <span
                                key={idx}
                                className="bg-primary/10 text-primary inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
                              >
                                {email}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {config.high_intent_emails && config.high_intent_emails.length > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <span
                            className="text-lg leading-none text-amber-600"
                            title="High Intent Notifications"
                          >
                            🔥
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {config.high_intent_emails.map((email) => (
                              <span
                                key={email}
                                className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900"
                              >
                                {email}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditConfig(config)}>
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteConfig(config)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                      Review Prompt
                    </p>
                    <p className="text-muted-foreground mt-1 line-clamp-3 text-sm">
                      {config.prompt}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ReviewConfigModal
        open={isModalOpen}
        onOpenChange={handleModalOpenChange}
        config={editingConfig}
        onSubmit={handleSubmitConfig}
        errors={modalErrors}
        isLoading={isSaving}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        resourceName="review config"
        resourceIdentifier={deleteTarget?.name}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};
